import { generateNanoid } from '../../utils/generateId';

const actions = {
  selectModality: 'SELECT_MODALITY',
  toggleDay: 'TOGGLE_DAY',
  updateDayHour: 'UPDATE_DAY_HOUR',
  removeDayHour: 'REMOVE_DAY_HOUR',
  addDayHour: 'ADD_DAY_HOUR',
  addExceptionDay: 'ADD_EXCEPTION_DAY',
  removeExceptionDay: 'REMOVE_EXCEPTION_DAY',
  updateExceptionDay: 'UPDATE_EXCEPTION_DAY',
  changeStatus: 'CHANGE_STATUS',
  loadSuccess: 'LOAD_SUCCESS',
};

const initialState = {
  entities: {
    delivery: {
      operatingDays: [],
      exceptionDays: [],
    },
    pickup: {
      operatingDays: [],
      exceptionDays: [],
    },
  },
  selectedId: 'delivery',
  status: 'loading',
};

const reducer = (state, action) => {
  switch (action.type) {
    case actions.selectModality:
      return {
        ...state,
        selectedId: action.payload,
      };
    case actions.toggleDay:
      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            operatingDays: state.entities[state.selectedId].operatingDays.map((day, index) =>
              index === action.payload.dayIndex ? { ...day, enabled: !day.enabled } : day
            ),
          },
        },
      };
    case actions.updateDayHour: {
      const { dayIndex, hourId, start, end } = action.payload;
      const hours = state.entities[state.selectedId].operatingDays[dayIndex].hours.map((hour) =>
        hour.id === hourId ? { ...hour, start, end } : hour
      );
      const operatingDays = state.entities[state.selectedId].operatingDays.map((day, index) =>
        index === dayIndex ? { ...day, hours } : day
      );

      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            operatingDays,
          },
        },
      };
    }
    case actions.removeDayHour: {
      const { dayIndex, hourId } = action.payload;
      const hours = state.entities[state.selectedId].operatingDays[dayIndex].hours.filter(
        (hour) => hour.id !== hourId
      );
      const operatingDays = state.entities[state.selectedId].operatingDays.map((day, index) =>
        index === dayIndex ? { ...day, hours } : day
      );

      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            operatingDays,
          },
        },
      };
    }
    case actions.addDayHour: {
      const { dayIndex } = action.payload;
      const currentHours = state.entities[state.selectedId].operatingDays[dayIndex].hours;
      let start = '00:00';

      if (
        currentHours.length &&
        !currentHours[currentHours.length - 1].end.toString().includes('23:59')
      ) {
        const currentEnd = currentHours[currentHours.length - 1].end;
        const [hours, minute] = currentEnd.split(':');
        const setPad = (time) => time.toString().padStart(2, '0');

        const date = new Date();
        date.setHours(hours, minute, 0);
        date.setMinutes(date.getMinutes() + 1);
        start = `${setPad(date.getHours())}:${setPad(date.getMinutes())}`;
      }

      const hours = [...currentHours, { start, end: '23:59', id: generateNanoid() }];
      const operatingDays = state.entities[state.selectedId].operatingDays.map((day, index) =>
        index === dayIndex ? { ...day, hours } : day
      );

      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            operatingDays,
          },
        },
      };
    }
    case actions.addExceptionDay:
      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            exceptionDays: [
              ...state.entities[state.selectedId].exceptionDays,
              {
                date: new Date(),
                startHour: '00:00',
                endHour: '23:59',
                isClosedAllDay: false,
                id: generateNanoid(),
              },
            ],
          },
        },
      };
    case actions.removeExceptionDay:
      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            exceptionDays: state.entities[state.selectedId].exceptionDays.filter(
              (exceptionDay) => exceptionDay.id !== action.payload.exceptionDayId
            ),
          },
        },
      };
    case actions.updateExceptionDay: {
      const { exceptionDayId, exceptionDay } = action.payload;
      return {
        ...state,
        entities: {
          ...state.entities,
          [state.selectedId]: {
            ...state.entities[state.selectedId],
            exceptionDays: state.entities[state.selectedId].exceptionDays.map(
              (exceptionDayFromStore) =>
                exceptionDayFromStore.id === exceptionDayId ? exceptionDay : exceptionDayFromStore
            ),
          },
        },
      };
    }
    case actions.changeStatus:
      return {
        ...state,
        status: action.payload,
      };
    case actions.loadSuccess:
      return {
        ...state,
        status: 'success',
        entities: {
          delivery: {
            operatingDays: toOperatingDays(action.payload.storeOpenHours.delivery),
            exceptionDays: toExceptionDays(action.payload.exceptionsHours.delivery),
          },
          pickup: {
            operatingDays: toOperatingDays(action.payload.storeOpenHours.pickup),
            exceptionDays: toExceptionDays(action.payload.exceptionsHours.pickup),
          },
        },
      };
    default:
      return state;
  }
};

const operatingDaysInitialData = Array.from({ length: 7 })
  .map(() => ({
    enabled: false,
    hours: [],
  }))
  .reduce((acc, current, index) => {
    acc[index] = current;
    return acc;
  }, {});

const toOperatingDays = (items) =>
  Object.values(
    items.reduce(
      (data, item) =>
        data[item.dayWeek]
          ? {
              ...data,
              [item.dayWeek]: {
                enabled: true,
                hours: [
                  ...data[item.dayWeek].hours,
                  { start: item.openHours, end: item.closeHours, id: generateNanoid() },
                ],
              },
            }
          : data,
      operatingDaysInitialData
    )
  );

const toExceptionDays = (items) =>
  items.map((item) => ({
    date: item.exceptionDate,
    startHour: item.openHours,
    endHour: item.closeHours,
    isClosedAllDay: item.close,
    id: generateNanoid(),
  }));

export const OpeningHoursState = {
  initialState,
  reducer,
  actions,
};

/*
EXAMPLE
  entities: {
    delivery: {
      operatingDays: [
        { enabled: true, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
      ],
      exceptionDays: [
        {
          date: '2024-08-16',
          startHour: '00:00',
          endHour: '23:59',
          isClosedAllDay: false,
        },
      ],
    },
    pickup: {
      operatingDays: [
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: true, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
        { enabled: false, hours: [{ start: '00:00', end: '23:59' }] },
      ],
      exceptionDays: [
        {
          date: '2024-08-16',
          startHour: '00:00',
          endHour: '23:59',
          isClosedAllDay: false,
        },
      ],
    },
  },
  selectedId: 'delivery',
  status: 'loading'
*/
